<template>
  <div class="p-6 max-w-full">
    <div class="mb-6 flex items-start justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">Facturas Electrónicas</h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm">POS2 — CFDIs timbrados ante el SAT</p>
      </div>
      <a
        :href="urlVerificarSatReal"
        target="_blank"
        class="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition flex items-center gap-2"
        title="Abre el verificador oficial del SAT con datos de una factura REAL (no sandbox)"
      >
        <span>🔍</span> Probar SAT real
      </a>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 flex flex-wrap gap-3 items-end">
      <div class="flex-1 min-w-48">
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Buscar</label>
        <input
          v-model="filtros.buscar"
          @keyup.enter="cargar(1)"
          type="text"
          placeholder="UUID, folio nota..."
          class="w-full px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Estado</label>
        <select v-model="filtros.estado" @change="cargar(1)" class="px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400">
          <option value="">Todos</option>
          <option value="vigente">Vigente</option>
          <option value="cancelada">Cancelada</option>
        </select>
      </div>
      <button @click="cargar(1)" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
      <button @click="limpiar" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-300 transition">Limpiar</button>
    </div>

    <!-- Tabla -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div v-if="cargando" class="py-16 text-center text-gray-400">Cargando...</div>
      <div v-else-if="facturas.length === 0" class="py-16 text-center text-gray-400">Sin facturas timbradas</div>
      <table v-else class="w-full text-sm text-left">
        <thead class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">
          <tr>
            <th class="px-4 py-3">UUID (Folio Fiscal)</th>
            <th class="px-4 py-3">Serie-Folio</th>
            <th class="px-4 py-3">Nota Origen</th>
            <th class="px-4 py-3 text-right">Subtotal</th>
            <th class="px-4 py-3 text-right">IVA</th>
            <th class="px-4 py-3 text-right">Total</th>
            <th class="px-4 py-3">Estado</th>
            <th class="px-4 py-3">Timbrada</th>
            <th class="px-4 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in facturas" :key="f.id" class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40">
            <td class="px-4 py-3 font-mono text-violet-700 dark:text-violet-400 text-xs">{{ f.uuid }}</td>
            <td class="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{{ f.serie }}-{{ f.folio_interno }}</td>
            <td class="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{{ f.nota?.folio ?? '—' }}</td>
            <td class="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{{ money(f.subtotal) }}</td>
            <td class="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{{ money(f.iva) }}</td>
            <td class="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-200">{{ money(f.total) }}</td>
            <td class="px-4 py-3">
              <span :class="badge(f.estado)" class="text-xs font-semibold px-2 py-0.5 rounded-full">
                {{ f.estado }}
              </span>
            </td>
            <td class="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{{ fecha(f.fecha_timbrado) }}</td>
            <td class="px-4 py-3">
              <div class="flex gap-2 justify-end">
                <button
                  @click="descargarPdf(f)"
                  class="px-2.5 py-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 transition text-xs font-semibold"
                  title="PDF generado desde el XML local"
                >
                  PDF
                </button>
                <button
                  @click="descargarXml(f)"
                  class="px-2.5 py-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 transition text-xs font-semibold"
                  title="XML local"
                >
                  XML
                </button>
                <button
                  @click="descargarPdfSW(f)"
                  class="px-2.5 py-1.5 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 transition text-xs font-semibold"
                  title="PDF oficial desde el PAC (SmarterWeb)"
                >
                  PDF&nbsp;SW
                </button>
                <button
                  @click="descargarXmlSW(f)"
                  class="px-2.5 py-1.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition text-xs font-semibold"
                  title="XML oficial desde el PAC (SmarterWeb)"
                >
                  XML&nbsp;SW
                </button>
                <a
                  :href="urlVerificarSat(f)"
                  target="_blank"
                  class="px-2.5 py-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200 transition text-xs font-semibold"
                  title="Abrir verificador del SAT con los 3 datos pre-llenados"
                >
                  SAT
                </a>
                <button
                  @click="copiarDatosSat(f)"
                  class="px-2.5 py-1.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition text-xs font-semibold"
                  title="Copiar UUID, RFC emisor, RFC receptor y total"
                >
                  Copiar datos
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div v-if="meta.last_page > 1" class="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
      <span>Página {{ meta.current_page }} de {{ meta.last_page }} — {{ meta.total }} resultados</span>
      <div class="flex gap-2">
        <button @click="cargar(meta.current_page - 1)" :disabled="meta.current_page <= 1"
          class="px-3 py-1 rounded border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Anterior</button>
        <button @click="cargar(meta.current_page + 1)" :disabled="meta.current_page >= meta.last_page"
          class="px-3 py-1 rounded border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Siguiente</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import api from '@/utils/axiosConfig'

const http     = useHttp()
const facturas = ref([])
const cargando = ref(false)
const meta     = ref({ current_page: 1, last_page: 1, total: 0 })
const filtros  = ref({ buscar: '', estado: '' })

// Botón de prueba con datos REALES de una factura productiva del SAT.
// El portal del SAT usa hash routing (#) — los params van DESPUÉS del #, no antes.
const urlVerificarSatReal = (() => {
  const uuid  = '37190c6e-dad9-4315-bcee-fb2392d32849'
  const re    = 'MCO101206GA7'
  const rr    = 'RMU061212CRA'
  const total = 1160.00
  const [ent, dec] = total.toFixed(6).split('.')
  const tt = `${ent.padStart(17, '0')}.${dec.padEnd(6, '0')}`
  return `https://verificacfdi.facturaelectronica.sat.gob.mx/default.aspx?&id=${uuid}&re=${re}&rr=${rr}&tt=${tt}&fe=`
})()

const cargar = async (pagina = 1) => {
  cargando.value = true
  try {
    const params = new URLSearchParams({ page: pagina, per_page: 15 })
    if (filtros.value.buscar) params.set('buscar', filtros.value.buscar)
    if (filtros.value.estado) params.set('estado', filtros.value.estado)

    await http.get(`/pos2/facturas?${params}`)
    const res = http.data.value ?? {}
    facturas.value = res.data ?? []
    meta.value = { current_page: res.current_page, last_page: res.last_page, total: res.total }
  } finally {
    cargando.value = false
  }
}

const limpiar = () => {
  filtros.value = { buscar: '', estado: '' }
  cargar(1)
}

const descargarPdf = async (f) => {
  try {
    const resp = await api.get(`/pos2/facturas/${f.id}/pdf`, { responseType: 'blob' })
    const blob = new Blob([resp.data], { type: 'application/pdf' })
    const url  = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  } catch (err) {
    alert(err.response?.data?.message ?? 'No se pudo generar el PDF.')
  }
}

const urlVerificarSat = (f) => {
  const re = f.emisor_rfc   || 'EKU9003173C9'
  const rr = f.receptor_rfc || 'XAXX010101000'
  // El SAT espera el total con 17 dígitos enteros, punto y 6 decimales.
  const totalNum  = Number(f.total ?? 0)
  const [ent, dec = ''] = totalNum.toFixed(6).split('.')
  const tt = `${ent.padStart(17, '0')}.${dec.padEnd(6, '0')}`
  return `https://verificacfdi.facturaelectronica.sat.gob.mx/default.aspx?&id=${f.uuid}&re=${re}&rr=${rr}&tt=${tt}&fe=`
}

const copiarDatosSat = async (f) => {
  const re = f.emisor_rfc   || 'EKU9003173C9'
  const rr = f.receptor_rfc || 'XAXX010101000'
  const total = Number(f.total ?? 0).toFixed(2)
  const texto = `Folio Fiscal: ${f.uuid}\nRFC Emisor: ${re}\nRFC Receptor: ${rr}\nTotal: ${total}`
  try {
    await navigator.clipboard.writeText(texto)
    alert('Datos copiados al portapapeles.\nPégalos en el verificador del SAT.')
  } catch {
    alert(texto)
  }
}

const descargarXml = async (f) => {
  try {
    const resp = await api.get(`/pos2/facturas/${f.id}/xml`, { responseType: 'text' })
    const blob = new Blob([resp.data], { type: 'application/xml' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `${f.uuid}.xml`
    a.click()
    URL.revokeObjectURL(url)
  } catch {
    alert('No se pudo descargar el XML.')
  }
}

// Descarga directa desde el PAC (SmarterWeb)
const descargarXmlSW = async (f) => {
  try {
    const resp = await api.get(`/pos2/facturas/${f.id}/xml-sw`, { responseType: 'text' })
    // Si el PAC respondió con JSON de error, lo detectamos
    if (typeof resp.data === 'string' && resp.data.trim().startsWith('{')) {
      try {
        const err = JSON.parse(resp.data)
        alert(err.message ?? 'El PAC no devolvió el XML.')
        return
      } catch { /* es XML válido */ }
    }
    const blob = new Blob([resp.data], { type: 'application/xml' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `${f.uuid}-sw.xml`
    a.click()
    URL.revokeObjectURL(url)
  } catch (err) {
    alert(err.response?.data?.message ?? 'No se pudo recuperar el XML desde el PAC.')
  }
}

const descargarPdfSW = async (f) => {
  try {
    const resp = await api.get(`/pos2/facturas/${f.id}/pdf-sw`, { responseType: 'blob' })
    const blob = new Blob([resp.data], { type: 'application/pdf' })
    const url  = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  } catch (err) {
    alert(err.response?.data?.message ?? 'No se pudo recuperar el PDF desde el PAC.')
  }
}

const fecha = (val) => val ? new Date(val).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'
const money = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val ?? 0)
const badge = (e) => ({
  vigente:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelada: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
}[e] ?? 'bg-gray-100 text-gray-600')

onMounted(() => cargar(1))
</script>
